import { observer } from "mobx-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMobxStores } from "../stores/stores";
import Loader from "./loaders/Loader";
import { Button, Col, Form, Input, Modal, Row, Select, Typography } from "antd";
import "./CatalogueItemDetailsModal.css";

const CatalogueItemDetailsModal = () => {
  const { catalogueStore } = useMobxStores();
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [initialValues, setInitialValues] = useState({});

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(true);
  const params = useParams();
  const [form] = Form.useForm();
  const title = Form.useWatch("title", form);
  const description = Form.useWatch("description", form);
  const source = Form.useWatch("source", form);
  const outcome = Form.useWatch("educational_outcome", form);
  const level = Form.useWatch("level", form);

  const navigate = useNavigate();

  // useEffect(() => {
  //   const fetchCatalogueItem = async () => {
  //     await catalogueStore.GetCatalogueById(params.catalogueId);
  //     setInitialValues(catalogueStore.catalogueItem);
  //     form.setFieldsValue(catalogueStore.catalogueItem);
  //     setLoading(false);
  //   };
  //   fetchCatalogueItem();
  // }, []);

  useEffect(() => {
    let cancelled = false;

    const fetchCatalogueItem = async () => {
      setLoading(true);

      try {
        await catalogueStore.GetCatalogueById(params.catalogueId);

        if (cancelled) return;

        // If your store sets catalogueItem to null/undefined on 404, guard it too:
        if (!catalogueStore.catalogueItem || !catalogueStore.catalogueItem.id) {
          setIsModalOpen(false);
          navigate("/catalogue", { replace: true });
          return;
        }

        setInitialValues(catalogueStore.catalogueItem);
        form.setFieldsValue(catalogueStore.catalogueItem);
        setLoading(false);
      } catch (err) {
        if (cancelled) return;

        // 404 or any error -> close modal & exit
        setIsModalOpen(false);
        setLoading(false);
        form.resetFields();

        // go back so URL /catalogue/:id doesn't remain
        navigate("/catalogue", { replace: true });
      }
    };

    // only fetch if it's a number
    if (/^\d+$/.test(params.catalogueId ?? "")) {
      fetchCatalogueItem();
    } else {
      // invalid id in url
      setIsModalOpen(false);
      navigate("/catalogue", { replace: true });
    }

    return () => {
      cancelled = true;
    };
  }, [params.catalogueId, catalogueStore, form, navigate]);

  useEffect(() => {
    if (!initialValues || Object.keys(initialValues).length === 0) {
      setIsSaveDisabled(true);
      return;
    }

    const hasEmptyFields = !title?.trim() || !description?.trim() || !level;

    const isSame =
      title === initialValues.title &&
      description === initialValues.description &&
      source === initialValues.source &&
      outcome === initialValues.educational_outcome &&
      level === initialValues.level;

    setIsSaveDisabled(hasEmptyFields || isSame);
  }, [title, description, source, outcome, level, initialValues]);

  const handleSave = async () => {
    const formData = new FormData();
    formData.append("title", form.getFieldValue("title"));
    formData.append("type", catalogueStore.catalogueItem.type);
    formData.append("description", form.getFieldValue("description"));
    formData.append("source", form.getFieldValue("source"));
    formData.append(
      "educational_outcome",
      form.getFieldValue("educational_outcome"),
    );
    formData.append("level", form.getFieldValue("level"));

    setLoading(true);
    await catalogueStore.UpdateCatalogueItemById(formData);
    setLoading(false);

    form.resetFields();
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleDelete = () => {
    catalogueStore.DeleteCatalogueItemById(catalogueStore.catalogueItem?.id);
    setIsDeleteModalOpen(false);
  };

  const handleCancelDelete = () => {
    setIsDeleteModalOpen(false);
    setIsModalOpen(true);
  };

  return (
    <>
      <Modal
        className="catalogue-item-details-modal"
        open={isModalOpen}
        onCancel={() => {
          form.resetFields();
          navigate("/catalogue");
        }}
        footer={
          loading ||
          catalogueStore.user.id !==
            catalogueStore.catalogueItem.owner ? null : isEditing ? (
            <Row justify={"end"} style={{ gap: "12px" }}>
              <Button
                type="primary"
                onClick={handleSave}
                disabled={isSaveDisabled}
              >
                Save
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </Row>
          ) : (
            <Row justify={"end"} style={{ gap: "12px" }}>
              <Button type="primary" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
              <Button
                onClick={() => {
                  setIsModalOpen(false);
                  setIsDeleteModalOpen(true);
                }}
              >
                Delete
              </Button>
            </Row>
          )
        }
      >
        <div className="catalogue-item-wrapper">
          {loading ? (
            <Loader />
          ) : (
            <>
              {!isEditing ? (
                <>
                  <div className="catalogue-title-container">
                    <div>
                      <Row className="catalogue-item-title">
                        {catalogueStore.catalogueItem?.title}
                      </Row>
                      <Row
                        justify={"space-between"}
                        style={{ display: "flex", alignItems: "end" }}
                      >
                        <Col>
                          <Typography
                            className="catalogue-item-owner"
                            onClick={() =>
                              navigate(
                                `/portfolio/${catalogueStore.catalogueItem?.owner_username}`,
                              )
                            }
                          >
                            @{catalogueStore.catalogueItem?.owner_username}
                          </Typography>
                        </Col>
                        <Col className="catalogue-item-type">
                          {catalogueStore.catalogueItem?.type}
                        </Col>
                      </Row>
                    </div>
                    <Row className="catalogue-item-description">
                      {catalogueStore.catalogueItem?.description}
                    </Row>
                  </div>
                  <Row>
                    <div className="catalogue-item-heading">Source:</div>
                    <div
                      className="catalogue-item-url"
                      onClick={() =>
                        (window.location.href = `${catalogueStore.catalogueItem?.source}`)
                      }
                    >
                      {catalogueStore.catalogueItem?.source}
                    </div>
                  </Row>
                  {catalogueStore.catalogueItem?.educational_outcome && (
                    <Row>
                      <div className="catalogue-item-heading">
                        Learning outcome:
                      </div>
                      <div className="catalogue-item-outcome">
                        {catalogueStore.catalogueItem?.educational_outcome}
                      </div>
                    </Row>
                  )}
                  {catalogueStore.catalogueItem?.level && (
                    <Row>
                      <div className="catalogue-item-heading">
                        Target Knowledge level:
                      </div>
                      <div className="catalogue-item-level">
                        {catalogueStore.catalogueItem?.level}
                      </div>
                    </Row>
                  )}
                </>
              ) : (
                <Form
                  form={form}
                  initialValues={catalogueStore.catalogueItem}
                  className="catalogue-item-edit-form"
                >
                  <div className="catalogue-title-container">
                    <Form.Item name="title" style={{ marginBottom: 0 }}>
                      <Input className="catalogue-item-title" />
                    </Form.Item>
                    <Row justify={"space-between"}>
                      <Col className="catalogue-item-owner">
                        @{catalogueStore.catalogueItem?.owner_username}
                      </Col>
                      <Col className="catalogue-item-type">
                        {catalogueStore.catalogueItem?.type}
                      </Col>
                    </Row>
                  </div>
                  <Form.Item name="description">
                    <Input.TextArea
                      rows={3}
                      className="catalogue-item-description"
                    />
                  </Form.Item>
                  <div>
                    <div className="catalogue-item-heading catalogue-item-heading-editing">
                      Source:
                    </div>
                    <Form.Item name="source" className="catalogue-item-form">
                      <Input className="catalogue-item-url" />
                    </Form.Item>
                  </div>
                  <div>
                    <div className="catalogue-item-heading catalogue-item-heading-editing">
                      Learning outcome:
                    </div>
                    <Form.Item
                      name="educational_outcome"
                      className="catalogue-item-form"
                      rules={[
                        {
                          max: 200,
                          message:
                            "Learning outcome must be 200 characters or fewer.",
                        },
                      ]}
                    >
                      <Input
                        className="catalogue-item-outcome"
                        maxLength={200}
                        showCount
                      />
                    </Form.Item>
                  </div>
                  <div>
                    <div className="catalogue-item-heading catalogue-item-heading-editing">
                      Target Knowledge level:
                    </div>
                    <Form.Item name="level" className="catalogue-item-form">
                      <Select className="catalogue-item-level">
                        <Select.Option value="beginner">Beginner</Select.Option>
                        <Select.Option value="intermediate">
                          Intermediate
                        </Select.Option>
                        <Select.Option value="advanced">Advanced</Select.Option>
                        {/* <Select.Option value="expert">Expert</Select.Option> */}
                      </Select>
                    </Form.Item>
                  </div>
                </Form>
              )}
            </>
          )}
        </div>
      </Modal>
      <Modal
        open={isDeleteModalOpen}
        onCancel={handleCancelDelete}
        footer={[
          <Button onClick={handleCancelDelete}>No</Button>,
          <Button type="primary" onClick={handleDelete}>
            Yes
          </Button>,
        ]}
      >
        Are you sure you want to delete this item?
      </Modal>
    </>
  );
};

export default observer(CatalogueItemDetailsModal);
